<template>
    <div class="run-javascript-attribute-picker">
        <div class="filter-menu">
            <SupsisButton v-if="selectedMenu.length" ghost small @click="prev">
                <span class="material-icons"> chevron_left </span>
            </SupsisButton>
            <div class="breadcrumb-item" v-for="menu in selectedMenu" :key="menu">
                <span> {{ findAttributeByValue(menu).label }} ✦ </span>
            </div>
        </div>

        <div class="javascript-blocks" v-if="!selectedMenu.length">
            <div class="block-item" v-for="block in javascriptBlocks" :key="block.id" @click="selectBlock(block)">
                <span class="material-icons">code</span>
                <span class="name">{{ block.name || "Run JavaScript" }}</span>
            </div>
        </div>

        <div class="attribute-list" v-else>
            <div class="attr-select">
                <div
                    class="picker-item"
                    v-for="attr in filterAttributes()"
                    @click="() => select(attr.value)"
                    :key="attr.value"
                >
                    <span class="name">{{ attr.label }}</span>
                    <span class="description" v-if="attr.description">{{ attr.description }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";
import flatDiagram from "../../../utils/flatDiagram";

export default {
    name: "RunJavaScriptAttributePicker",
    components: {
        SupsisButton,
    },
    props: {
        onPicked: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            searchText: "",
            attributes: [],
            selectedMenu: [],
            javascriptBlocks: [],
            selectedBlock: null,
        };
    },
    computed: {
        ...mapGetters({
            board: "CB/board",
        }),
    },
    methods: {
        listJavaScriptBlocks() {
            const storyId = this.$route.params.id;
            const board = this.board(storyId);
            const flatted = flatDiagram(board.diagram);
            return flatted.filter((block) => block.type === "RUN_JAVASCRIPT");
        },

        generateAttributesForBlock(block) {
            const blockId = block.id;
            const basePrefix = `block.${blockId}`;
            const exampleResult = block.payload?.exampleResult;

            // exampleResult yoksa veya boşsa placeholder attribute'lar göster
            if (!exampleResult || Object.keys(exampleResult).length === 0) {
                return [
                    {
                        label: "*",
                        value: `${basePrefix}`,
                        description: "Tüm değerler (blok henüz çalıştırılmadı)",
                    },
                    {
                        label: "success",
                        value: `${basePrefix}.success`,
                        description: "İşlem başarılı mı? (boolean)",
                    },
                    {
                        label: "result",
                        value: `${basePrefix}.result`,
                        description: "İşlem sonucu (object)",
                    },
                    {
                        label: "error",
                        value: `${basePrefix}.error`,
                        description: "Hata mesajı (varsa)",
                    }
                ];
            }

            // exampleResult varsa, içeriğine göre attribute'ları oluştur
            return this.generateDataAttributes(basePrefix, exampleResult);
        },

        generateDataAttributes(prefix, data) {
            const attributes = [];

            if (!data) return [];

            if (Array.isArray(data) && data.length > 0) {
                // Array için wildcard
                attributes.push({
                    label: "*",
                    value: `${prefix}.$..*`,
                    description: "Tüm array elemanları",
                });

                const firstItem = data[0];
                if (firstItem && typeof firstItem === "object") {
                    Object.entries(firstItem).forEach(([key, value]) => {
                        const _prefix = `${prefix}.${key}`;
                        const subAttributes = this.generateDataAttributes(_prefix, value);
                        attributes.push({
                            label: key,
                            value: _prefix,
                            description: `${key} (${this.getValueType(value)})`,
                            subAttributes,
                        });
                    });
                }
            } else if (typeof data === "object" && data !== null) {
                // Object için wildcard
                attributes.push({
                    label: "*",
                    value: `${prefix}.$..*`,
                    description: "Tüm object alanları",
                });
                
                Object.entries(data).forEach(([key, value]) => {
                    const _prefix = `${prefix}.${key}`;
                    const subAttributes = this.generateDataAttributes(_prefix, value);
                    
                    attributes.push({
                        label: key,
                        value: _prefix,
                        description: `${key} (${this.getValueType(value)})`,
                        subAttributes: subAttributes && subAttributes.length > 0 ? subAttributes : undefined,
                    });
                });
            } else {
                // Primitive değer, alt attribute yok
                return undefined;
            }

            return attributes.length > 0 ? attributes : undefined;
        },

        getValueType(value) {
            if (value === null) return "null";
            if (Array.isArray(value)) return "array";
            if (typeof value === "object") return "object";
            if (typeof value === "string") return "string";
            if (typeof value === "number") return "number";
            if (typeof value === "boolean") return "boolean";
            return "unknown";
        },

        selectBlock(block) {
            this.selectedBlock = block;
            this.attributes = this.generateAttributesForBlock(block);
            this.selectedMenu.push("root");
        },

        filterAttributes() {
            const parentAttribute = this.getParentAttribute();
            let attrs = parentAttribute ? parentAttribute.subAttributes : this.attributes;

            return attrs.filter((attr) => {
                if (this.searchText) {
                    return (attr.label || "").toLowerCase().includes(this.searchText.toLowerCase());
                }
                return true;
            });
        },

        getParentAttribute() {
            if (this.selectedMenu.length <= 1) return null;
            const attributes = this.getFlatAttributes();
            const parent = this.selectedMenu[this.selectedMenu.length - 1];
            return attributes.find((x) => x.value === parent);
        },

        getFlatAttributes() {
            const flat = (attr) => {
                if (attr.subAttributes) {
                    return [attr, ...attr.subAttributes.flatMap(flat)];
                }
                return [attr];
            };
            return this.attributes.flatMap(flat);
        },

        select(value) {
            const attributes = this.getFlatAttributes();
            const attr = attributes.find((x) => x.value === value);
            if (Array.isArray(attr.subAttributes) && attr.subAttributes.length) {
                this.selectedMenu.push(value);
            } else {
                this.onPicked(value);
            }
        },

        prev() {
            if (this.selectedMenu.length === 1) {
                this.selectedMenu = [];
                this.selectedBlock = null;
            } else {
                this.selectedMenu.pop();
            }
        },

        findAttributeByValue(value) {
            if (value === "root") {
                return { label: this.selectedBlock?.name || "Run JavaScript" };
            }
            const attributes = this.getFlatAttributes();
            return attributes.find((x) => x.value === value);
        },
    },
    created() {
        this.javascriptBlocks = this.listJavaScriptBlocks();
    },
};
</script>

<style lang="scss" scoped>
.run-javascript-attribute-picker {
    background: #fff;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    max-height: 600px;
    min-height: 200px;
    user-select: none;
    width: 100%;
    padding: 5px;
    overflow: hidden; // Ana container'da taşmayı engelle

    .filter-menu {
        display: flex;
        align-items: center;
        gap: 3px;
        margin-bottom: 10px;
        flex-shrink: 0; // Menü küçülmesin

        .supsis-button {
            border: none !important;
            font-size: 13px;
            height: 25px;
        }

        .breadcrumb-item {
            display: flex;
        }
    }

    .javascript-blocks {
        flex: 1; // Kalan alanı doldur
        overflow-y: auto; // Scroll ekle
        overflow-x: hidden; // Yatay taşmayı engelle
        padding: 5px;
        margin: 0px 5px;

        // Scroll bar styling
        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;

            &:hover {
                background: #a8a8a8;
            }
        }

        .block-item {
            display: flex;
            align-items: center;
            padding: 8px;
            cursor: pointer;
            border-radius: 4px;
            margin-bottom: 4px;

            &:hover {
                background-color: #f0f4f7;
            }

            .material-icons {
                margin-right: 8px;
                color: #ff6b35;
            }

            .name {
                color: #22272d;
                font-size: 14px;
            }
        }
    }

    .attribute-list {
        flex: 1; // Kalan alanı doldur
        overflow-y: auto; // Scroll ekle
        overflow-x: hidden; // Yatay taşmayı engelle
        padding: 5px;
        margin: 0px 5px;

        // Scroll bar styling
        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;

            &:hover {
                background: #a8a8a8;
            }
        }

        .picker-item {
            align-items: center;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            padding: 8px;
            transition: background-color 0.2s;
            border-radius: 4px;

            &:hover {
                background-color: #f0f4f7;
            }

            .name {
                color: #22272d;
                font-size: 14px;
            }

            .description {
                color: #9ba6b3;
                font-size: 12px;
            }
        }
    }
}
</style>

